import {
  getCurrentSubscription,
  isPushSupported,
  serializeSubscription,
  subscribe,
  unsubscribe
} from '@mmmike/web-push/client';
import type { Branch } from './domain';
import { getDeviceIdentity } from './sync';

export type PushState = 'loading' | 'unsupported' | 'denied' | 'available' | 'enabled';

async function uploadSubscription(subscription: PushSubscription, branches: Branch[]) {
  const { deviceId } = await getDeviceIdentity();
  const response = await fetch('/api/push', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deviceId,
      ...serializeSubscription(subscription),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Bangkok',
      branches: branches.map(({ id, name, region }) => ({ id, name, region }))
    })
  });
  if (!response.ok) throw new Error(response.status === 503 ? 'Push notifications are not configured' : 'Unable to save push subscription');
}

export async function readPushState(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission !== 'granted') return 'available';
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return 'available';
  return await registration.pushManager.getSubscription() ? 'enabled' : 'available';
}

export async function enablePushNotifications(branches: Branch[]) {
  const configResponse = await fetch('/api/push', { headers: { Accept: 'application/json' } });
  if (!configResponse.ok) throw new Error(configResponse.status === 503 ? 'Push notifications are not configured' : 'Unable to load push configuration');
  const config = await configResponse.json() as { publicKey?: string };
  if (!config.publicKey) throw new Error('Push notifications are not configured');

  await navigator.serviceWorker.register('/sw.js');
  const result = await subscribe(config.publicKey);
  if (result.status === 'unsupported') return 'unsupported' as const;
  if (result.status === 'denied') return 'denied' as const;
  await uploadSubscription(result.subscription, branches);
  return 'enabled' as const;
}

export async function syncPushSubscription(branches: Branch[]) {
  if (!isPushSupported() || Notification.permission !== 'granted') return false;
  const subscription = await getCurrentSubscription();
  if (!subscription) return false;
  await uploadSubscription(subscription, branches);
  return true;
}

export async function disablePushNotifications() {
  if (!isPushSupported()) return;
  const current = await getCurrentSubscription();
  if (!current) return;
  const { deviceId } = await getDeviceIdentity();
  const endpoint = current.endpoint;
  await unsubscribe();
  const response = await fetch('/api/push', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deviceId, endpoint })
  });
  if (!response.ok) throw new Error('Unable to remove push subscription');
}
